import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  Home,
  ChevronRight,
  Target,
  Eye,
  ShieldCheck,
  CheckCircle2,
  Building2,
  Landmark,
  Globe2,
  Plus,
  Minus,
  Sparkles,
} from "lucide-react";

import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

const heroBg = "/assets/images/bg2.jpeg";

const sectionBg =
  "https://images.unsplash.com/photo-1494412574643-ff11b0a5c1c3?q=80&w=1800&auto=format&fit=crop";

const tabs = [
  {
    id: "vision",
    title: "Our Vision",
    icon: Eye,
    body:
      "To be the premier agency of government responsible for promoting, securing and sustaining investments in the nation’s oil and gas free zones.",
  },
  {
    id: "mission",
    title: "Our Mission",
    icon: Target,
    body:
      "To facilitate public-private partnership investments in the nation’s oil and gas free zones as vehicles for promoting accelerated growth and sustainable development.",
  },
  {
    id: "values",
    title: "Core Values",
    icon: Sparkles,
    body:
      "Integrity, Respect for self, clients, time and colleagues, Passion, Customer Centricity, Creativity, Transparency and Accountability, and Professionalism.",
  },
];

const mandates = [
  {
    id: 1,
    title: "Licensing & Regulation",
    description:
      "License, regulate, supervise, manage, control and co-ordinate the activities of Oil and Gas Free Zones across Nigeria.",
  },
  {
    id: 2,
    title: "Investment Approvals",
    description:
      "Grant requisite permits and licences to all prospective investors wishing to do business within Oil and Gas Free Zones in Nigeria.",
  },
  {
    id: 3,
    title: "Investor Incentives",
    description:
      "Offer incentives to free zone enterprises and ensure that the maximum incentives and operational benefits are passed to registered companies.",
  },
  {
    id: 4,
    title: "Investment Promotion",
    description:
      "Promote the Oil and Gas Free Zones to local and international investors seeking opportunities within Nigeria’s energy and industrial sectors.",
  },
];

const functions = [
  "Administration and management of the Oil and Gas Free Zones.",
  "Grant of all requisite permits and licences to approved enterprises operating within the zones.",
  "Approval of development plans and annual budgets relating to infrastructure and operational facilities.",
  "Establishment of customs, immigration, police and related security posts within the zones.",
  "Supervision and coordination of public and private organizations operating within the free zones.",
  "Resolution of trade disputes between employers and employees in consultation with the Federal Ministry of Labour and Productivity.",
];

const values = [
  "Integrity",
  "Respect",
  "Passion",
  "Customer Centric",
  "Creativity",
  "Transparency & Accountability",
  "Professionalism",
];

const VisionMissionMandate = () => {
  const pageRef = useRef(null);

  const [active, setActive] = useState("vision");
  const [openMandate, setOpenMandate] = useState(1);

  const current = tabs.find((item) => item.id === active);

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.from(".hero-badge", {
        opacity: 0,
        y: 30,
        duration: 0.8,
        ease: "power3.out",
      });

      gsap.from(".hero-title", {
        opacity: 0,
        y: 80,
        duration: 1.2,
        ease: "power3.out",
      });

      gsap.from(".hero-text", {
        opacity: 0,
        y: 40,
        delay: 0.2,
        duration: 1,
        ease: "power3.out",
      });

      gsap.utils.toArray(".reveal-up").forEach((el) => {
        gsap.from(el, {
          opacity: 0,
          y: 70,
          duration: 0.9,
          ease: "power3.out",
          scrollTrigger: {
            trigger: el,
            start: "top 85%",
          },
        });
      });

      gsap.utils.toArray(".value-card").forEach((el, index) => {
        gsap.from(el, {
          opacity: 0,
          y: 45,
          scale: 0.96,
          delay: index * 0.08,
          duration: 0.8,
          ease: "power3.out",
          scrollTrigger: {
            trigger: el,
            start: "top 90%",
          },
        });
      });

      gsap.to(".floating-orb-1", {
        y: 50,
        x: -20,
        duration: 7,
        repeat: -1,
        yoyo: true,
        ease: "sine.inOut",
      });

      gsap.to(".floating-orb-2", {
        y: -50,
        x: 20,
        duration: 8,
        repeat: -1,
        yoyo: true,
        ease: "sine.inOut",
      });
    }, pageRef);

    return () => ctx.revert();
  }, []);

  return (
    <main ref={pageRef} className="bg-white overflow-hidden">
      {/* HERO SECTION */}
      <section className="relative min-h-screen flex items-center overflow-hidden text-white">
        <img
          src={heroBg}
          alt="OGFZA"
          className="absolute inset-0 w-full h-full object-cover object-center"
        />

        <div className="absolute inset-0 bg-[#041018]/85" />

        <div className="absolute inset-0 bg-gradient-to-br from-[#082567]/95 via-[#026B07]/70 to-[#F2AF11]/35" />

        <div className="floating-orb-1 absolute top-0 left-0 w-[500px] h-[500px] rounded-full bg-[#082567]/40 blur-3xl" />

        <div className="floating-orb-2 absolute bottom-0 right-0 w-[500px] h-[500px] rounded-full bg-[#F2AF11]/20 blur-3xl" />

        <div className="absolute inset-0 bg-[linear-gradient(to_right,rgba(255,255,255,0.05)_1px,transparent_1px),linear-gradient(to_bottom,rgba(255,255,255,0.05)_1px,transparent_1px)] bg-[size:80px_80px]" />

        <div className="relative max-w-7xl mx-auto px-6 py-32">
          <div className="hero-badge inline-flex items-center gap-2 px-5 py-2 rounded-full bg-white/10 border border-white/15 backdrop-blur-md mb-8">
            <span className="w-2 h-2 rounded-full bg-[#F2AF11]" />
            OGFZA Corporate Overview
          </div>

          <h1
            className="hero-title text-6xl md:text-8xl font-black leading-[0.95] max-w-5xl"
            style={{ fontFamily: "'Alegreya Sans', sans-serif" }}
          >
            Vision, Mission & Mandate
          </h1>

          <p className="hero-text mt-8 max-w-3xl text-white/85 text-lg leading-[1.9]">
            The Oil and Gas Free Zones Authority is Nigeria’s national
            regulatory agency responsible for promoting, supervising, and
            sustaining investments within the nation’s Oil and Gas Free Zones.
          </p>

          <div className="hero-text flex items-center gap-2 mt-8 text-sm text-white/75">
            <Link
              to="/"
              className="flex items-center gap-1 hover:text-[#F2AF11] transition-colors"
            >
              <Home size={14} />
              Home
            </Link>

            <ChevronRight size={14} />

            <Link
              to="/about"
              className="hover:text-[#F2AF11] transition-colors"
            >
              About Us
            </Link>

            <ChevronRight size={14} />

            <span className="text-[#F2AF11]">
              Vision, Mission & Mandate
            </span>
          </div>
        </div>
      </section>

      {/* ABOUT */}
      <section className="py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-20 items-center">
          <div className="relative reveal-up">
            <div className="absolute -top-10 -left-10 w-52 h-52 rounded-full bg-[#082567]/10 blur-3xl" />

            <div className="relative rounded-[40px] overflow-hidden shadow-[0_40px_120px_-20px_rgba(8,37,103,0.35)]">
              <img
                src={sectionBg}
                alt="OGFZA"
                className="w-full h-[700px] object-cover"
              />

              <div className="absolute inset-0 bg-gradient-to-t from-[#041018]/90 via-transparent to-transparent" />

              <div className="absolute bottom-8 left-8 right-8 bg-white rounded-3xl p-7 shadow-2xl">
                <p className="text-[#F2AF11] font-black tracking-[0.2em] uppercase text-xs mb-2">
                  About OGFZA
                </p>

                <h3
                  className="text-[#082567] text-3xl font-black leading-tight"
                  style={{ fontFamily: "'Alegreya Sans', sans-serif" }}
                >
                  Oil & Gas Free Zones Authority
                </h3>
              </div>
            </div>
          </div>

          <div className="reveal-up">
            <p className="text-[#026B07] font-black tracking-[0.2em] uppercase text-xs mb-4">
              OGFZA In Brief
            </p>

            <h2
              className="text-5xl md:text-6xl font-black leading-tight text-[#082567] mb-8"
              style={{ fontFamily: "'Alegreya Sans', sans-serif" }}
            >
              Regulating Nigeria’s Strategic Oil & Gas Free Zones
            </h2>

            <div className="space-y-6 text-slate-700 leading-[2] text-[16.5px]">
              <p>
                The Oil and Gas Free Zones Authority (OGFZA) is the national
                regulatory agency superintending over the operation of the oil
                and gas free trade zones in the country.
              </p>

              <p>
                OGFZA was established by Section 2 of the Oil and Gas Export
                Free Zone Act No. 8 of 1996 to regulate Nigeria’s Oil and Gas
                Free Trade Zones.
              </p>

              <p>
                The Authority commenced operations in Onne, Rivers State in
                2001, beginning with the Onne Oil and Gas Free Zone as the first
                dedicated oil and gas free zone in Nigeria.
              </p>

              <p>
                Through strategic public-private partnerships and investor
                focused reforms, OGFZA continues to position Nigeria’s oil and
                gas free zones as globally competitive investment destinations.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* VISION & MISSION */}
      <section className="py-32 bg-[#F5F7FA]">
        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-20 items-center">
          <div className="reveal-up">
            <p className="text-[#026B07] font-black tracking-[0.2em] uppercase text-xs mb-4">
              Institutional Direction
            </p>

            <h2
              className="text-5xl md:text-6xl font-black text-[#082567] leading-tight mb-10"
              style={{ fontFamily: "'Alegreya Sans', sans-serif" }}
            >
              Building Investor Confidence Through Strategic Regulation
            </h2>

            <div className="flex flex-wrap gap-4 mb-8">
              {tabs.map((tab) => {
                const Icon = tab.icon;

                return (
                  <button
                    key={tab.id}
                    onClick={() => setActive(tab.id)}
                    className={`px-6 py-4 rounded-2xl flex items-center gap-3 transition-all duration-300 ${
                      active === tab.id
                        ? "bg-[#082567] text-white shadow-xl"
                        : "bg-white text-[#082567] hover:bg-[#082567] hover:text-white"
                    }`}
                  >
                    <Icon size={20} />
                    <span className="font-black">{tab.title}</span>
                  </button>
                );
              })}
            </div>

            <div className="bg-white border border-slate-100 rounded-[35px] p-10 shadow-[0_40px_100px_-20px_rgba(8,37,103,0.18)]">
              <h3
                className="text-3xl font-black text-[#082567] mb-5"
                style={{ fontFamily: "'Alegreya Sans', sans-serif" }}
              >
                {current.title}
              </h3>

              <p className="text-slate-700 leading-[2] text-[16px]">
                {current.body}
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 reveal-up">
            <div className="bg-white rounded-[32px] p-8 shadow-xl border border-slate-100">
              <div className="w-16 h-16 rounded-2xl bg-[#082567]/10 text-[#082567] flex items-center justify-center mb-6">
                <Building2 size={30} />
              </div>

              <h3 className="text-2xl font-black text-[#082567] mb-4">
                Regulatory Excellence
              </h3>

              <p className="text-slate-600 leading-[1.9]">
                OGFZA promotes globally competitive free zone standards through
                effective regulation and operational efficiency.
              </p>
            </div>

            <div className="bg-[#082567] rounded-[32px] p-8 shadow-xl text-white">
              <div className="w-16 h-16 rounded-2xl bg-white/10 flex items-center justify-center mb-6">
                <Globe2 size={30} />
              </div>

              <h3 className="text-2xl font-black mb-4">
                Global Investments
              </h3>

              <p className="text-white/80 leading-[1.9]">
                Creating investor-friendly environments that attract local and
                international participation.
              </p>
            </div>

            <div className="bg-[#026B07] rounded-[32px] p-8 shadow-xl text-white">
              <div className="w-16 h-16 rounded-2xl bg-white/10 flex items-center justify-center mb-6">
                <Landmark size={30} />
              </div>

              <h3 className="text-2xl font-black mb-4">
                National Development
              </h3>

              <p className="text-white/80 leading-[1.9]">
                Supporting economic growth, industrialization and employment
                generation across Nigeria.
              </p>
            </div>

            <div className="bg-white rounded-[32px] p-8 shadow-xl border border-slate-100">
              <div className="w-16 h-16 rounded-2xl bg-[#F2AF11]/15 text-[#F2AF11] flex items-center justify-center mb-6">
                <ShieldCheck size={30} />
              </div>

              <h3 className="text-2xl font-black text-[#082567] mb-4">
                Investor Protection
              </h3>

              <p className="text-slate-600 leading-[1.9]">
                Ensuring transparent operations, investment security and
                investor confidence.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* MANDATE */}
      <section className="relative py-32 overflow-hidden">
        <div className="absolute inset-0 bg-[#041018]" />

        <div className="absolute inset-0 bg-gradient-to-br from-[#082567]/95 via-[#026B07]/70 to-[#F2AF11]/20" />

        <div className="relative max-w-7xl mx-auto px-6 grid lg:grid-cols-[0.9fr_1.1fr] gap-16 items-start">
          <div className="reveal-up">
            <p className="text-[#F2AF11] font-black tracking-[0.2em] uppercase text-xs mb-4">
              Our Mandate
            </p>

            <h2
              className="text-5xl md:text-6xl font-black text-white leading-tight mb-6"
              style={{ fontFamily: "'Alegreya Sans', sans-serif" }}
            >
              Facilitating Sustainable Investment Growth
            </h2>

            <p className="text-white/85 leading-[1.9] text-[17px]">
              OGFZA is charged with promoting, supervising, regulating and
              coordinating all Oil and Gas Free Zones in Nigeria while ensuring
              maximum investor incentives and operational excellence.
            </p>
          </div>

          <div className="space-y-4 reveal-up">
            {mandates.map((item) => {
              const open = openMandate === item.id;

              return (
                <div
                  key={item.id}
                  className="bg-white/95 rounded-[24px] overflow-hidden shadow-xl"
                >
                  <button
                    onClick={() => setOpenMandate(open ? null : item.id)}
                    className={`w-full flex items-center justify-between gap-5 px-6 py-5 transition-all duration-300 ${
                      open
                        ? "bg-[#082567] text-white"
                        : "bg-white text-[#082567] hover:bg-[#082567] hover:text-white"
                    }`}
                  >
                    <span className="flex items-center gap-4">
                      <span
                        className={`w-11 h-11 rounded-2xl flex items-center justify-center font-black ${
                          open
                            ? "bg-[#F2AF11] text-white"
                            : "bg-[#082567]/10 text-[#082567]"
                        }`}
                      >
                        {String(item.id).padStart(2, "0")}
                      </span>

                      <span className="font-black text-[17px]">
                        {item.title}
                      </span>
                    </span>

                    {open ? <Minus size={22} /> : <Plus size={22} />}
                  </button>

                  <div
                    className={`overflow-hidden transition-all duration-500 ${
                      open ? "max-h-56" : "max-h-0"
                    }`}
                  >
                    <p className="px-6 py-5 text-slate-700 leading-[1.9] text-[15.5px] border-t border-slate-100">
                      {item.description}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* FUNCTIONS */}
      <section className="py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="max-w-3xl mx-auto text-center reveal-up mb-20">
            <p className="text-[#026B07] font-black tracking-[0.2em] uppercase text-xs mb-4">
              Functions of OGFZA
            </p>

            <h2
              className="text-5xl md:text-6xl font-black text-[#082567] leading-tight mb-6"
              style={{ fontFamily: "'Alegreya Sans', sans-serif" }}
            >
              Statutory Responsibilities of The Authority
            </h2>
          </div>

          <div className="grid lg:grid-cols-2 gap-8">
            {functions.map((item, index) => (
              <div
                key={index}
                className="reveal-up bg-[#F5F7FA] rounded-[30px] p-8 border border-[#E2E8F0] hover:shadow-2xl transition-all duration-500"
              >
                <div className="flex gap-5">
                  <div className="w-14 h-14 rounded-2xl bg-[#082567] text-white flex items-center justify-center flex-shrink-0">
                    <CheckCircle2 size={24} />
                  </div>

                  <p className="text-slate-700 leading-[1.9] text-[16px]">
                    {item}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CORE VALUES */}
      <section className="py-28 bg-white relative overflow-hidden">
        <div className="absolute top-0 left-0 w-[500px] h-[500px] rounded-full bg-[#082567]/5 blur-3xl" />

        <div className="absolute bottom-0 right-0 w-[500px] h-[500px] rounded-full bg-[#F2AF11]/10 blur-3xl" />

        <div className="relative max-w-7xl mx-auto px-6">
          <div className="max-w-3xl mx-auto text-center mb-16 reveal-up">
            <p className="text-[#026B07] font-black tracking-[0.2em] uppercase text-xs mb-4">
              Core Values
            </p>

            <h2
              className="text-4xl md:text-5xl font-black text-[#082567] leading-tight"
              style={{ fontFamily: "'Alegreya Sans', sans-serif" }}
            >
              The Principles Guiding OGFZA
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {values.slice(0, 6).map((item, index) => (
              <div
                key={index}
                className="value-card group bg-white border border-[#E2E8F0] rounded-[24px] px-6 py-7 flex items-center gap-4 shadow-[0_20px_60px_-45px_rgba(15,23,42,0.25)] transition-all duration-500 hover:bg-[#082567] hover:border-[#082567] hover:-translate-y-2 hover:shadow-[0_35px_90px_-40px_rgba(8,37,103,0.55)]"
              >
                <div className="w-14 h-14 rounded-2xl bg-[#082567]/10 text-[#082567] flex items-center justify-center flex-shrink-0 transition-all duration-500 group-hover:bg-white/10 group-hover:text-[#F2AF11]">
                  <ShieldCheck size={24} />
                </div>

                <h3 className="text-lg md:text-xl font-black text-[#082567] leading-tight transition-colors duration-500 group-hover:text-white">
                  {item}
                </h3>
              </div>
            ))}
          </div>

          <div className="flex justify-center mt-6">
            <div className="value-card group w-full md:w-[32%] bg-white border border-[#E2E8F0] rounded-[24px] px-6 py-7 flex items-center gap-4 shadow-[0_20px_60px_-45px_rgba(15,23,42,0.25)] transition-all duration-500 hover:bg-[#082567] hover:border-[#082567] hover:-translate-y-2 hover:shadow-[0_35px_90px_-40px_rgba(8,37,103,0.55)]">
              <div className="w-14 h-14 rounded-2xl bg-[#082567]/10 text-[#082567] flex items-center justify-center flex-shrink-0 transition-all duration-500 group-hover:bg-white/10 group-hover:text-[#F2AF11]">
                <ShieldCheck size={24} />
              </div>

              <h3 className="text-lg md:text-xl font-black text-[#082567] leading-tight transition-colors duration-500 group-hover:text-white">
                {values[6]}
              </h3>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default VisionMissionMandate;